import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Field, FormBanner, Honeypot, SelectInput, TextArea, TextInput } from "@/components/public/FormKit";

const EMPTY = {
  nominee_name: "",
  category: "",
  nominator_name: "",
  nominator_email: "",
  nominator_phone: "",
  nominee_bio: "",
  nomination_reason: "",
  consent: false,
  honeypot: "",
};

/** Single-page nomination form, matching the approved Emerald Honours mockup. */
export function NominationFormCard() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  useEffect(() => {
    api.get("/public/emerald/config").then((r) => setConfig(r.data)).catch(() => setConfig(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (form.nominee_name.trim().length < 2) next.nominee_name = "Please enter the nominee's name.";
    if (!form.category) next.category = "Please choose a category.";
    if (form.nominator_name.trim().length < 2) next.nominator_name = "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.nominator_email))
      next.nominator_email = "Please enter a valid email address.";
    if (!form.nominator_phone.trim()) next.nominator_phone = "Please enter your phone number.";
    if (form.nominee_bio.trim().length < 10) next.nominee_bio = "Please tell us about the nominee.";
    if (form.nomination_reason.trim().length < 20)
      next.nomination_reason = "Please tell us why this person should be recognised.";
    if (!form.consent) next.consent = "Please acknowledge the privacy notice to continue.";
    setErrors(next);
    if (Object.keys(next).length) {
      setBanner({ status: "error", message: "Please review the highlighted fields." });
      return;
    }
    setBanner({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/public/emerald/nominations", form);
      setBanner({ status: "ok", message: data.message });
      setForm(EMPTY);
    } catch (err) {
      setBanner({ status: "error", message: apiError(err) });
    }
  };

  if (config && config.nominations_open === false) {
    return (
      <div
        data-testid="emerald-inline-form-closed"
        className="rounded-3xl bg-white p-7 shadow-[0_24px_60px_-30px_rgba(11,53,84,0.35)] lg:p-8"
        role="status"
      >
        <h2 className="font-display text-xl font-bold text-bka-navy">Nominations are closed</h2>
        <p className="mt-3 text-sm leading-relaxed text-bka-ink/75">{config.closed_message}</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white p-6 shadow-[0_24px_60px_-30px_rgba(11,53,84,0.35)] sm:p-7 lg:p-8">
      <h2 className="font-display text-xl font-bold text-bka-navy lg:text-2xl">Submit a Nomination</h2>
      <p className="mt-2.5 text-sm leading-relaxed text-bka-ink/75">
        Help us celebrate individuals who are making a difference. Complete the form below to submit your
        nomination.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="emerald-inline-nomination-form" noValidate>
        <Honeypot value={form.honeypot} onChange={set("honeypot")} />

        <Field label="Nominee Name" htmlFor="in-nominee" required error={errors.nominee_name}>
          <TextInput
            id="in-nominee"
            data-testid="emerald-inline-nominee-name"
            value={form.nominee_name}
            onChange={set("nominee_name")}
            error={errors.nominee_name}
            placeholder="Enter nominee's full name"
          />
        </Field>

        <Field label="Category" htmlFor="in-category" required error={errors.category}>
          <SelectInput
            id="in-category"
            data-testid="emerald-inline-category"
            value={form.category}
            onChange={set("category")}
            error={errors.category}
          >
            <option value="">Select a category</option>
            {(config?.categories || []).map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </SelectInput>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your Name" htmlFor="in-name" required error={errors.nominator_name}>
            <TextInput
              id="in-name"
              data-testid="emerald-inline-nominator-name"
              value={form.nominator_name}
              onChange={set("nominator_name")}
              error={errors.nominator_name}
              placeholder="Enter your full name"
            />
          </Field>
          <Field label="Your Email" htmlFor="in-email" required error={errors.nominator_email}>
            <TextInput
              id="in-email"
              type="email"
              data-testid="emerald-inline-nominator-email"
              value={form.nominator_email}
              onChange={set("nominator_email")}
              error={errors.nominator_email}
              placeholder="Enter your email address"
            />
          </Field>
        </div>

        <Field label="Your Phone" htmlFor="in-phone" required error={errors.nominator_phone}>
          <TextInput
            id="in-phone"
            data-testid="emerald-inline-nominator-phone"
            value={form.nominator_phone}
            onChange={set("nominator_phone")}
            error={errors.nominator_phone}
            placeholder="Enter your phone number"
          />
        </Field>

        <Field label="Nominee Bio / Background" htmlFor="in-bio" required error={errors.nominee_bio}>
          <TextArea
            id="in-bio"
            data-testid="emerald-inline-bio"
            rows={4}
            value={form.nominee_bio}
            onChange={set("nominee_bio")}
            error={errors.nominee_bio}
            placeholder="Tell us about the nominee (their work, achievements, etc.)"
          />
        </Field>

        <Field label="Reason for Nomination" htmlFor="in-reason" required error={errors.nomination_reason}>
          <TextArea
            id="in-reason"
            data-testid="emerald-inline-reason"
            rows={4}
            value={form.nomination_reason}
            onChange={set("nomination_reason")}
            error={errors.nomination_reason}
            placeholder="Why do you believe this person should be recognised?"
          />
        </Field>

        <div>
          <label htmlFor="in-consent" className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-bka-ink/80">
            <input
              id="in-consent"
              data-testid="emerald-inline-consent"
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--bka-emerald)]"
            />
            <span>
              I confirm this information is accurate and understand my details will be used only to review
              and follow up on this nomination.
            </span>
          </label>
          {errors.consent && (
            <p className="mt-1.5 text-xs font-semibold text-red-600">{errors.consent}</p>
          )}
        </div>

        <FormBanner status={banner.status} message={banner.message} testId="emerald-inline-banner" />

        <button
          type="submit"
          data-testid="emerald-inline-submit"
          disabled={banner.status === "loading"}
          className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-bka-emerald px-6 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-bka-emeraldDark hover:shadow-[0_14px_32px_-14px_rgba(0,140,114,0.9)] disabled:opacity-60"
        >
          {banner.status === "loading" ? "Submitting…" : "Submit Nomination"}
          <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </button>
      </form>
    </div>
  );
}
